/**
 * Application-access schemas.
 *
 * App access is the "who can reach this application" surface — a
 * set of group grants and direct user grants per application. The
 * enforcement path (backend/internal/tenant/application.IsAuthorized)
 * stops a user from obtaining an OIDC token or SAML assertion if
 * they don't show up in either grant set.
 *
 * Two wire shapes live here:
 *
 *   1. AccessRosterEntry — the cross-app read view used by the
 *      admin "Policies & access" matrix. One row per application,
 *      enriched with display_names and group member counts so the
 *      list renders from a single round-trip.
 *
 *   2. AccessSet — the per-app write view used by the editor. Just
 *      the granted user + group UUIDs; display names are looked up
 *      from the tenant's /users and /groups endpoints.
 *
 * Wire mirrors pkg/api/tenant/application.go — drift is a P0.
 */

import { z } from "zod";

// ── Cross-app roster (list view) ────────────────────────────────────────────

export const accessRosterGroupEntrySchema = z.object({
  group_id: z.string(),
  display_name: z.string(),
  member_count: z.number().int().nonnegative(),
  granted_at: z.string(),
});
export type AccessRosterGroupEntry = z.infer<typeof accessRosterGroupEntrySchema>;

export const accessRosterUserEntrySchema = z.object({
  user_id: z.string(),
  display_name: z.string(),
  email: z.string(),
  granted_at: z.string(),
});
export type AccessRosterUserEntry = z.infer<typeof accessRosterUserEntrySchema>;

export const accessRosterEntrySchema = z.object({
  application_id: z.string(),
  application_name: z.string(),
  protocol: z.enum(["oidc", "saml", "smart_on_fhir", "private_access"]),
  status: z.enum(["active", "disabled", "deleted"]),
  has_icon: z.boolean(),
  icon_updated_at: z.string().optional(),
  updated_at: z.string(),
  /** MAX(granted_at) across both grant tables for this app.
   *  Omitted when the app has no grants. Distinct from updated_at
   *  (the applications row's own modification time) — a grant
   *  mutation never touches the apps row itself, so labeling the
   *  row "updated {relative time}" in the UI was misleading. */
  latest_grant_at: z.string().optional(),
  /** applications.access_version at read time. Sent back on the
   *  per-app edit page as expected_version for optimistic
   *  concurrency. Non-negative int64. */
  version: z.number().int().nonnegative(),
  groups: z.array(accessRosterGroupEntrySchema),
  users: z.array(accessRosterUserEntrySchema),
});
export type AccessRosterEntry = z.infer<typeof accessRosterEntrySchema>;

export const accessRosterResponseSchema = z.object({
  data: z.array(accessRosterEntrySchema),
  pagination: z.object({
    total: z.number().int().nonnegative(),
    offset: z.number().int().nonnegative(),
    limit: z.number().int().nonnegative(),
  }),
});
export type AccessRosterResponse = z.infer<typeof accessRosterResponseSchema>;

// ── Per-app access set (write view) ─────────────────────────────────────────

export const accessGrantSchema = z.object({
  application_id: z.string(),
  subject_kind: z.enum(["user", "group"]),
  user_id: z.string().optional(),
  group_id: z.string().optional(),
  granted_at: z.string(),
  granted_by: z.string().nullable().optional(),
  /** Display name of the group or user at read time. Empty
   *  string when the referenced subject has been soft-deleted —
   *  the editor surfaces such rows as orphans so the operator
   *  can strip them. */
  display_name: z.string().optional(),
  /** Primary email — only meaningful for user grants. */
  email: z.string().optional(),
  /** Live member count — only meaningful for group grants. */
  member_count: z.number().int().nonnegative().optional(),
  /** True only for a directly granted user whose tenant record is active. */
  pilot_eligible: z.boolean(),
});
export type AccessGrant = z.infer<typeof accessGrantSchema>;

export const accessSetSchema = z.object({
  groups: z.array(accessGrantSchema),
  users: z.array(accessGrantSchema),
  /** Optimistic-concurrency counter. Threaded back into
   *  ReplaceAccessGroups/Users mutations as expected_version so two
   *  admins editing the same app's roster can't silently clobber
   *  each other — the second PUT returns 409
   *  (application-access-version-conflict). */
  version: z.number().int().nonnegative(),
});
export type AccessSet = z.infer<typeof accessSetSchema>;

// ── Mutation input ──────────────────────────────────────────────────────────

export const replaceAccessGroupsSchema = z.object({
  group_ids: z.array(z.string()),
  /** Optional — supply to enable the concurrency guard. The editor
   *  always supplies it; setup scripts that predate the guard
   *  remain compatible by omitting it (last-write-wins). */
  expected_version: z.number().int().nonnegative().optional(),
});
export type ReplaceAccessGroupsInput = z.infer<typeof replaceAccessGroupsSchema>;

export const replaceAccessUsersSchema = z.object({
  user_ids: z.array(z.string()),
  expected_version: z.number().int().nonnegative().optional(),
});
export type ReplaceAccessUsersInput = z.infer<typeof replaceAccessUsersSchema>;
